import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IuserState } from "./user.types";
import axiosInstance from "config/axios";

const initialState: IuserState = {
  user: {
    _id: "",
    isAdmin: false,
    isHospital: true,
    fullName: "",
    hospitalName: "",
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    phone: 0,
    address: "",
    address2: "",
    city: "",
    country: "",
    state: "",
    zipCode: "",
    profile: "",
  },
  users: [],
  userError: {},
  isUserActivityInProcess: false,
  successMesssage: "",
};

//to show user info
export const getLoggedInUser = createAsyncThunk(
  "getLoggedInUser/user",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance().get("/user/get-loggedin-user");
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response);
    }
  }
);

export const getAllUsers = createAsyncThunk(
  "getAllUsers/user",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance().get("/user/get-all-users");
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response);
    }
  }
);

export const updateUser = createAsyncThunk(
  "updateUser/user",
  async (user: any, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance().put(
        "/user/update-user-info",
        user
      );

      return data;
    } catch (error: any) {
      return rejectWithValue(error.response);
    }
  }
);

export const updateUserProfileImage = createAsyncThunk(
  //to show pic
  "updateUserProfileImage/user",
  async (user: any, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance().put(
        "/user/update-user-profile",
        user
      );
      //   ;
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response);
    }
  }
);

export const removeUserProfileImage = createAsyncThunk(
  //to show pic
  "removeUserProfileImage/user",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance().put("/user/remove-user-profile");
      //   ;
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response);
    }
  }
);

export const updateUserPassword = createAsyncThunk(
  "updateUserPassword/user",
  async (user: any, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance().post("/auth/reset-password", user);
      //   ;
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response);
    }
  }
);

export const getSingleUser = createAsyncThunk(
  "getSingleUser/pet",
  async (userID: any, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance().get(`/user/get-user/${userID}`);

      return data;
    } catch (error: any) {
      return rejectWithValue(error.response);
    }
  }
);

export const updateSubscription = createAsyncThunk(
  "updateUser/subscription",
  async (payload: any, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance().put(
        `/kit/update-subscription/${payload?.kitId}`,
        { subscription: payload?.subscription }
      );
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response);
    }
  }
);

const userSlice = createSlice({
  name: "userSlice",
  initialState: { ...initialState, name: "userInitialState" },
  reducers: {
    errorClean: (state) => {
      state.userError = {
        code: null,
        message: "",
        error: "",
      };
    },
    messageClean: (state) => {
      state.successMesssage = "";
    },
    userClean: (state) => {
      state.user = {
        _id: "",
        isAdmin: false,
        isHospital: true,
        fullName: "",
        email: "",
        password: "",
        hospitalName: "",
        firstName: "",
        lastName: "",
        phone: 0,
        address: "",
        address2: "",
        city: "",
        country: "",
        state: "",
        zipCode: "",
        profile: "",
      };
    },
  },
  extraReducers: (builder) => {
    // get loggedin user cases
    builder.addCase(getLoggedInUser.pending, (state, action: any) => {
      state.isUserActivityInProcess = true;
    });

    builder.addCase(getLoggedInUser.rejected, (state, action: any) => {
      state.isUserActivityInProcess = false;
      state.userError = {
        code: action.payload?.status,
        message: action.payload?.data.message,
      };
    });

    builder.addCase(getLoggedInUser.fulfilled, (state, action: any) => {
      state.isUserActivityInProcess = false;
      state.successMesssage = action.payload.message;
      state.user = action.payload.user;
    });

    // get all users
    builder.addCase(getAllUsers.pending, (state, action: any) => {
      state.isUserActivityInProcess = true;
    });

    builder.addCase(getAllUsers.rejected, (state, action: any) => {
      state.isUserActivityInProcess = false;
      state.userError = {
        code: action.payload.status,
        message: action.payload.data.message,
      };
    });

    builder.addCase(getAllUsers.fulfilled, (state, action: any) => {
      state.isUserActivityInProcess = false;
      state.successMesssage = action.payload.message;
      state.users = action.payload.users;
    });

    // get single user detail cases
    builder.addCase(getSingleUser.pending, (state, action: any) => {
      state.isUserActivityInProcess = true;
    });

    builder.addCase(getSingleUser.rejected, (state, action: any) => {
      state.isUserActivityInProcess = false;
      state.userError = {
        code: action.payload.status,
        message: action.payload.data.message,
      };
    });

    builder.addCase(getSingleUser.fulfilled, (state, action: any) => {
      state.isUserActivityInProcess = false;
      state.successMesssage = action.payload.message;
      state.singleUser = action.payload.user;
    });

    // update user image cases
    builder.addCase(updateUserProfileImage.pending, (state, action: any) => {
      state.isUserActivityInProcess = true;
    });

    builder.addCase(updateUserProfileImage.rejected, (state, action: any) => {
      state.isUserActivityInProcess = false;
      state.userError = {
        code: action.payload.status,
        message: action.payload.data.message,
      };
    });

    builder.addCase(updateUserProfileImage.fulfilled, (state, action: any) => {
      state.isUserActivityInProcess = false;
      state.successMesssage = action.payload.message;
      state.user = action.payload.user;
    });

    //remove user image case
    builder.addCase(removeUserProfileImage.pending, (state, action: any) => {
      state.isUserActivityInProcess = true;
    });

    builder.addCase(removeUserProfileImage.rejected, (state, action: any) => {
      state.isUserActivityInProcess = false;
      state.userError = {
        code: action.payload.status,
        message: action.payload.data.message,
      };
    });

    builder.addCase(removeUserProfileImage.fulfilled, (state, action: any) => {
      state.isUserActivityInProcess = false;
      state.successMesssage = action.payload.message;

      state.user = action.payload.user;
    });

    //update User Info
    builder.addCase(updateUser.pending, (state, action: any) => {
      state.isUserActivityInProcess = true;
    });

    builder.addCase(updateUser.rejected, (state, action: any) => {
      state.isUserActivityInProcess = false;
      state.userError = {
        code: action.payload.status,
        message: action.payload.data.message,
      };
    });

    builder.addCase(updateUser.fulfilled, (state, action: any) => {
      state.isUserActivityInProcess = false;
      state.successMesssage = action.payload.message;

      state.user = action.payload.user;
    });

    // update password cases
    builder.addCase(updateUserPassword.pending, (state, action: any) => {
      state.isUserActivityInProcess = true;
    });

    builder.addCase(updateUserPassword.rejected, (state, action: any) => {
      state.isUserActivityInProcess = false;
      state.userError = {
        code: action.payload.status,
        message: action.payload.data.message,
      };
    });

    builder.addCase(updateUserPassword.fulfilled, (state, action: any) => {
      state.isUserActivityInProcess = false;
      state.successMesssage = action.payload.message;
      state.user = action.payload.user;
    });
  },
});
export const { errorClean, messageClean, userClean } = userSlice.actions;

export default userSlice.reducer;
